import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  Rating,
  Divider,
  Avatar
} from "@mui/material";
import { deepOrange } from "@mui/material/colors";
import Swal from "sweetalert2";
import axios from "axios";
import * as Yup from "yup";
import { Constants } from "../../endPoints";

// Yup schema
const reviewSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  summary: Yup.string().nullable(),
  review: Yup.string().required("Review is required"),
  quality: Yup.number()
    .min(1, "Rating must be at least 1")
    .max(5, "Rating cannot be more than 5")
    .required("Rating is required"),
});

const Review = () => {
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    summary: "",
    review: "",
    quality: 0,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    try {
      await reviewSchema.validate(formData, { abortEarly: false });
      setErrors({});
      setLoading(true);

      await axios.post(Constants.addProductReview, {
        idProduct: id,
        name: formData.name,
        summary: formData.summary,
        review: formData.review,
        rating: formData.quality,
        status: "pending",
      });

      Swal.fire({
        icon: "success",
        title: "Review Submitted!",
        text: "Thank you for your feedback. It will be published once approved.",
        confirmButtonColor: "#A27800",
      });

      setFormData({ name: "", summary: "", review: "", quality: 0 });
      fetchReviews(); // refresh list
    } catch (err) {
      if (err.name === "ValidationError") {
        const formattedErrors = {};
        err.inner.forEach((e) => {
          formattedErrors[e.path] = e.message;
        });
        setErrors(formattedErrors);
      } else {
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: err?.response?.data?.message || err.message,
          confirmButtonColor: "#A27800",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${Constants.getApprovedReviewsByidProduct}/${id}`);
      setReviews(res.data.data || []);
    } catch (error) {
      console.error("Error loading approved reviews:", error);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [id]);

  const averageRating =
    reviews.length > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0;

  return (
    <Box sx={{ p: 4, backgroundColor: "#f9fafb" }}>

      {reviews.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Box textAlign="center" mb={2}>
            <Rating value={Math.round(averageRating)} precision={0.5} readOnly />
            <Typography variant="body2" color="primary">
              {averageRating.toFixed(2)} | Based on {reviews.length} Review
              {reviews.length > 1 ? "s" : ""}
            </Typography>
          </Box>

          {reviews.map((review) => (
            <Box key={review.id} mb={3}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ bgcolor: deepOrange[500], width: 30, height: 30 }}>
                    {review.name?.charAt(0)}
                  </Avatar>
                  <Typography fontWeight={600}>{review.name}</Typography>
                </Box>
                <Typography color="error" fontWeight={600} fontSize="14px">
                  {new Date(review.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                  })}
                </Typography>

              </Box>
              <Rating value={review.rating} readOnly sx={{ mt: 1 }} />
              {review.summary && (
                <Typography variant="subtitle2" mt={1}>
                  {review.summary}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {review.review}
              </Typography>
              <Divider sx={{ my: 2 }} />
            </Box>
          ))}
        </Box>
      )}

      {/* ✍️ Review Form */}
      <Typography variant="h5" fontWeight={500} mb={3}>
        Write Your Review
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography fontWeight={500} mb={1}>
            Your Rating *
          </Typography>
          <Rating
            name="quality"
            value={formData.quality}
            onChange={(e, newValue) =>
              setFormData((prev) => ({ ...prev, quality: newValue }))
            }
          />
          {errors.quality && (
            <Typography color="error" variant="caption">
              {errors.quality}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Summary"
            name="summary"
            value={formData.summary}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Review"
            name="review"
            value={formData.review}
            onChange={handleChange}
            error={!!errors.review}
            helperText={errors.review}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              bgcolor: "#A27800",
              color: "#fff",
              fontWeight: 600,
              px: 4,
              "&:hover": { bgcolor: "#000" },
              height: "54px",
            }}
          >
            {loading ? "Submitting..." : "Submit Review"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Review;
